import { Box, CircularProgress, Typography } from "@mui/material";
import styled from "@emotion/styled";

import { getLookupByCode, getLookupDescriptionByCode } from "../../../utilities/helpers";
import EqipRichTextDisplay from "../../../components/EqipRichTextDisplay";
import AdministrativeReport from "./AdministrativeReport";

const GeneralReport = ({ inspection, inspectionDetails, lookupsByGeneral, lookups, assignment, leadUser, allowedAssignTos, detailsLoading }) => {

  const SectionContainer = styled(Box)(({ theme }) => ({
    marginBottom: 32,
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  }));

  const GroupHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '1.3rem',
    color: '#2c3e50',
    marginBottom: 24,
    paddingBottom: 12,
    borderBottom: '3px solid #3498db',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }));

  const ItemHeader = styled(Typography)(({ theme }) => ({
    fontWeight: '600',
    fontSize: '1.1rem',
    color: '#34495e',
    marginTop: 20,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '2px solid #ecf0f1',
  }));

  const ContentSection = styled(Box)(({ theme }) => ({
    marginBottom: 16,
    padding: '16px',
    backgroundColor: '#fafbfc',
    borderRadius: '6px',
    border: '1px solid #e8ebee',
    '& p': {
      margin: 0,
      lineHeight: 1.7,
      color: '#2c3e50',
    },
    '& div': {
      lineHeight: 1.7,
      color: '#2c3e50',
    }
  }));

  // filter quality_protocol by code and sort by code for consistent ordering
  const foundProtocolQualities = inspection?.protocol_quality?.filter((protocol_quality) => {
    return protocol_quality?.code?.startsWith("SM");
  }).map(protocol_quality => {
    return {
      ...protocol_quality,
      detail: inspectionDetails?.find(detail =>
        detail.inpsection_detail_uid === protocol_quality.inpsection_detail_uid &&
        detail.code === protocol_quality.code)?.detail
    }
  }).sort((a, b) => {
    return a.code.localeCompare(b.code);
  }); // Sort by code for consistent ordering

  // Sort general groups with "Administrative Data" first, then alphabetically
  const priorityOrder = ['summary', 'administrative data'];

const foundGeneralGroups = lookupsByGeneral
  ?.filter((lookupByGeneral) => lookupByGeneral.parent_uid === null)
  ?.filter((lookupByGeneral) => !lookupByGeneral.description.toLowerCase().includes('system choice'))
  ?.sort((a, b) => {
    const aIndex = priorityOrder.findIndex((p) => a.description.toLowerCase().includes(p));
    const bIndex = priorityOrder.findIndex((p) => b.description.toLowerCase().includes(p));

    // If both are in the priority list, sort by priority
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

    // If one is in priority list, it comes first
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // Otherwise sort alphabetically
    return a.code.localeCompare(b.code);
  });

  return (
    foundGeneralGroups &&
    foundProtocolQualities && (
      <Box sx={{ padding: 2 }}>
        {foundGeneralGroups.map((group) => {
          // Sort protocol qualities within each group by code in ascending order
          const foundProtocolQualitiesByGroup = foundProtocolQualities?.filter((foundProtocolQuality) => 
            getLookupByCode(lookups, foundProtocolQuality.code)?.parent_uid === group.lookup_uid
          ).sort((a, b) => {
            return a.code.localeCompare(b.code);
          });
          
          // Check if this group has administrative data
          const hasAdministrativeData = group.description.toLowerCase().includes('administrative data');
          
          // Only render section if it has content (protocol qualities or administrative data)
          const hasContent = foundProtocolQualitiesByGroup.length > 0 || hasAdministrativeData;
          
          if (!hasContent) {
            return null;
          }
          
          return (
            <SectionContainer key={group.lookup_uid} id={`group-${group.lookup_uid}`} sx={{ scrollMarginTop: '100px' }}>
              <GroupHeader>
                {group.description}
              </GroupHeader>

              {hasAdministrativeData && (
                <Box id="administrative-section" sx={{ 
                  marginBottom: 3,
                  padding: 3,
                  backgroundColor: "#fafbfc",
                  borderRadius: "8px",
                  border: "1px solid #e8ebee",
                  scrollMarginTop: '100px'
                }}>
                  <AdministrativeReport
                    formData={{
                      inspectedFirm: assignment?.audited_firm_name,
                      location: assignment?.audited_firm_address,
                      phone: assignment?.audited_firm_phone,
                      fax: assignment?.audited_firm_fax,
                      mailingAddress: assignment?.audited_firm_address,
                      inspectionDates: assignment?.inspection_dates?.join(", "),
                      daysInFacility: assignment?.inspection_dates?.length || 0,
                      participants: allowedAssignTos?.length
                        ? allowedAssignTos.map((p) => `${p.user_name} - ${p.roleName}`).join('\n')
                        : (leadUser || assignment?.assign_to)
                    }}
                    setFormData={() => { }}
                  />
                </Box>
              )}

              {detailsLoading && foundProtocolQualitiesByGroup.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, color: 'text.secondary' }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading content...</Typography>
                </Box>
              )}
              {!detailsLoading && foundProtocolQualitiesByGroup.map((foundProtocolQuality) => {
                if (!foundProtocolQuality?.detail?.coverageComment?.trim()) {
                  return null;
                }

                return (
                  <Box key={foundProtocolQuality.code} sx={{ marginBottom: 3 }}>
                    <ItemHeader>
                      {getLookupDescriptionByCode(lookups, foundProtocolQuality?.code)}
                    </ItemHeader>

                    <ContentSection>
                      <EqipRichTextDisplay>
                        <div
                          style={{ lineHeight: "1.7" }}
                          dangerouslySetInnerHTML={{
                            __html: foundProtocolQuality?.detail?.coverageComment || "",
                          }}
                        />
                      </EqipRichTextDisplay>
                    </ContentSection>
                  </Box>
                );
              })}
            </SectionContainer>
          );
        })}
      </Box>
    )
  );
};

export default GeneralReport;
